import { useState, useMemo, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  GridReadyEvent,
  GridApi,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { getClients } from '../../shared/api/clients.api'
import { PageLoader } from '../../shared/ui/Spinner'
import { CategoryBadge } from '../../shared/ui/Badge'
import { EmptyState } from '../../shared/ui/EmptyState'
import { useAuthStore } from '../auth/authStore'
import { CheckboxSetFilter } from '../table/CheckboxSetFilter'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { normalizeCity } from '@crm/shared'
import type { ClientCategory } from '../../shared/api/types'
import { useGridSelection } from '../../shared/hooks/useGridSelection'
import { useAgGridScrollRestoration } from '../../shared/hooks/useScrollRestoration'

ModuleRegistry.registerModules([AllCommunityModule])

const COL_STATE_KEY = (userId: string) => `crm_clients_cols_v1_${userId}`

export function ClientsPage() {
  const { user } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()

  const category = (searchParams.get('category') ?? '') as ClientCategory | ''
  // Доп. фильтры из URL — приходят с дашборда (клик по сектору donut/heatmap).
  // Применяются на клиенте поверх загруженного списка.
  const managerIdsParam = searchParams.get('managerId') ?? ''
  const managerIdsFilter = useMemo(
    () => (managerIdsParam ? new Set(managerIdsParam.split(',').filter(Boolean)) : null),
    [managerIdsParam],
  )
  const cityFilter = searchParams.get('city') ?? ''
  const segmentFilter = searchParams.get('segment') ?? ''
  const externalFiltersActive = Boolean(managerIdsFilter || cityFilter || segmentFilter)
  const clearExternalFilters = () => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p)
      next.delete('managerId')
      next.delete('city')
      next.delete('segment')
      return next
    })
  }
  const [searchText, setSearchText] = useState('')

  // Единый сеттер URL-параметра: пустое значение удаляет параметр.
  const setUrlParam = (key: string, val: string) => {
    setSearchParams((p) => {
      const next = new URLSearchParams(p)
      if (val) next.set(key, val)
      else next.delete(key)
      return next
    })
  }

  const setCategory = (val: ClientCategory | '') => setUrlParam('category', val)

  const { data, isLoading } = useQuery({
    queryKey: ['clients', { category }],
    // limit большой — грузим всю клиентскую базу: фильтры город/сегмент/
    // менеджер и поиск работают на фронте, при загрузке куска счётчики
    // («найдено N») и дропдауны считали бы только по первым строкам.
    queryFn: () => getClients({ category: category || undefined, limit: 20000 }),
  })

  const rowData = useMemo(() => {
    const items = data?.items ?? []
    const filtered = items.filter((c) => {
      if (managerIdsFilter && !(c.managerId && managerIdsFilter.has(c.managerId))) return false
      // Город из URL — нормализованный (с дашборда). Сравниваем с нормализованным
      // городом клиента, чтобы «г. Москва» / «Москва» считались одним городом.
      if (cityFilter && (normalizeCity(c.city) || 'Без города') !== cityFilter) return false
      if (segmentFilter && (c.segment ?? '') !== segmentFilter) return false
      return true
    })
    return filtered.map((c) => ({
      id: c.id,
      name: c.name,
      code: c.code ?? '—',
      inn: c.inn ?? '—',
      city: c.city ?? '—',
      segment: c.segment ?? '—',
      category: c.category,
      requestsCount: c._count?.requests ?? 0,
      manager: c.manager?.name || c.manager?.email || '—',
    }))
  }, [data?.items, managerIdsFilter, cityFilter, segmentFilter])

  const canSeeManagers =
    user?.role === 'ADMIN' ||
    user?.role === 'GENERAL_DIRECTOR' ||
    user?.role === 'ROP' ||
    user?.role === 'ASSISTANT' ||
    user?.role === 'ACCOUNTANT'

  // Опции выпадающих фильтров — собираются из загруженного списка клиентов.
  // При активном фильтре по категории список (а значит и опции) сужается —
  // это ожидаемо: «менеджеры/города/сегменты в рамках выбранной категории».
  const filterOptions = useMemo(() => {
    const items = data?.items ?? []
    const cities = new Set<string>()
    const segments = new Set<string>()
    const managers = new Map<string, string>()
    for (const c of items) {
      // Город нормализуем — фильтр сравнивает normalizeCity(c.city) с
      // выбранным значением. Если в опции положить сырой c.city, выбор
      // «Ростов-на-Дону» не совпал бы с нормализованным «Ростов-На-Дону».
      const city = normalizeCity(c.city)
      if (city) cities.add(city)
      if (c.segment) segments.add(c.segment)
      if (c.managerId) {
        managers.set(c.managerId, c.manager?.name || c.manager?.email || c.managerId)
      }
    }
    const byRu = (a: string, b: string) => a.localeCompare(b, 'ru')
    return {
      cities: [...cities].sort(byRu),
      segments: [...segments].sort(byRu),
      managers: [...managers.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => byRu(a.name, b.name)),
    }
  }, [data?.items])

  // Селектор менеджера показывает значение, только если в URL ровно один id
  // (с дашборда может прийти несколько через запятую — тогда селект пустой).
  const managerSelectValue =
    managerIdsFilter && managerIdsFilter.size === 1 ? [...managerIdsFilter][0]! : ''

  const colDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Название',
        minWidth: 200,
        flex: 2,
        cellRenderer: ({ data }: { data: (typeof rowData)[0] }) => (
          <Link
            to={`/clients/${data.id}`}
            className="text-brand-600 hover:text-brand-700 font-medium"
          >
            {data.name}
          </Link>
        ),
      },
      {
        field: 'code',
        headerName: 'Код клиента',
        minWidth: 120,
        cellClass: 'font-mono text-xs text-gray-500',
      },
      {
        field: 'inn',
        headerName: 'ИНН',
        minWidth: 130,
        cellClass: 'font-mono text-xs text-gray-500',
      },
      {
        field: 'city',
        headerName: 'Город',
        minWidth: 130,
        cellClass: 'text-xs text-gray-600',
      },
      {
        field: 'segment',
        headerName: 'Сегмент',
        minWidth: 130,
        cellClass: 'text-xs text-gray-600',
      },
      {
        field: 'category',
        headerName: 'Кат.',
        minWidth: 90,
        cellRenderer: ({ data }: { data: (typeof rowData)[0] }) => (
          <CategoryBadge category={data.category} />
        ),
      },
      {
        field: 'requestsCount',
        headerName: 'Заявок',
        minWidth: 100,
        cellClass: 'text-gray-600',
      },
      ...(canSeeManagers
        ? [
            {
              field: 'manager',
              headerName: 'Менеджер',
              minWidth: 160,
              cellClass: 'text-xs text-gray-700',
            } as ColDef,
          ]
        : []),
    ],
    [canSeeManagers],
  )

  const gridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)

  // Восстановление позиции скролла таблицы клиентов при возврате из карточки.
  const gridContainerRef = useRef<HTMLDivElement>(null)
  useAgGridScrollRestoration('clients-grid', gridContainerRef)

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      filter: CheckboxSetFilter,
      floatingFilter: false,
      cellStyle: getCellStyle,
    }),
    [getCellStyle],
  )

  const saveState = useCallback(
    (api: GridApi) => {
      if (!user?.id) return
      try {
        localStorage.setItem(COL_STATE_KEY(user.id), JSON.stringify(api.getColumnState()))
      } catch {
        /* ignore */
      }
    },
    [user?.id],
  )

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (!user?.id) return
      const saved = localStorage.getItem(COL_STATE_KEY(user.id))
      if (saved) {
        try {
          params.api.applyColumnState({ state: JSON.parse(saved), applyOrder: true })
        } catch {
          /* ignore */
        }
      }
    },
    [user?.id],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Клиенты</h1>
          {data && (
            <span className="text-sm text-gray-500">
              {/* При любом активном фильтре (категория серверная,
                  город/сегмент/менеджер — клиентские) показываем число
                  отфильтрованных строк rowData, а не общий data.total. */}
              {category || externalFiltersActive ? 'найдено: ' : 'всего в базе: '}
              <span className="font-medium text-gray-900">
                {category || externalFiltersActive ? rowData.length : data.total}
              </span>
            </span>
          )}
        </div>
        {(user?.role === 'ADMIN' ||
          user?.role === 'MANAGER' ||
          user?.role === 'ROP' ||
          user?.role === 'ASSISTANT' ||
          user?.role === 'ACCOUNTANT') && (
          <Link
            data-testid="create-client-link"
            to="/clients/new"
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
          >
            + Новый клиент
          </Link>
        )}
      </div>

      {externalFiltersActive && (
        <div className="flex items-center gap-2 flex-wrap text-xs bg-brand-50 border border-brand-100 rounded-lg px-3 py-2">
          <span className="text-gray-500">Активные фильтры:</span>
          {managerIdsFilter && (
            <span className="px-2 py-0.5 bg-white rounded-md border border-brand-200">
              {managerSelectValue
                ? `менеджер: ${
                    filterOptions.managers.find((m) => m.id === managerSelectValue)?.name ??
                    managerSelectValue
                  }`
                : `менеджеры: ${managerIdsFilter.size}`}
            </span>
          )}
          {cityFilter && (
            <span className="px-2 py-0.5 bg-white rounded-md border border-brand-200">
              город: {cityFilter}
            </span>
          )}
          {segmentFilter && (
            <span className="px-2 py-0.5 bg-white rounded-md border border-brand-200">
              сегмент: {segmentFilter}
            </span>
          )}
          <button
            onClick={clearExternalFilters}
            className="text-gray-600 hover:text-gray-900 underline underline-offset-2"
          >
            Сбросить
          </button>
          <span className="text-gray-400 ml-auto">показано {rowData.length}</span>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Поиск по таблице..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white flex-1 min-w-[200px] max-w-md"
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            className="text-xs text-gray-400 hover:text-gray-700"
            title="Очистить поиск"
          >
            × Очистить
          </button>
        )}
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ClientCategory | '')}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">Все категории</option>
          <option value="A">Категория A</option>
          <option value="B">Категория B</option>
          <option value="C">Категория C</option>
        </select>
        {canSeeManagers && (
          <select
            value={managerSelectValue}
            onChange={(e) => setUrlParam('managerId', e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          >
            <option value="">Все менеджеры</option>
            {filterOptions.managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={cityFilter}
          onChange={(e) => setUrlParam('city', e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">Все города</option>
          {filterOptions.cities.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={segmentFilter}
          onChange={(e) => setUrlParam('segment', e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">Все сегменты</option>
          {filterOptions.segments.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : !rowData.length ? (
        <EmptyState title="Клиентов не найдено" />
      ) : (
        <div
          ref={gridContainerRef}
          className="crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm h-[calc(100vh-220px)]"
        >
          <AgGridReact
            ref={gridRef}
            getRowId={({ data }) => data.id}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            localeText={AG_GRID_LOCALE_RU}
            quickFilterText={searchText}
            onGridReady={onGridReady}
            onCellMouseDown={onCellMouseDown}
            onCellMouseOver={onCellMouseOver}
            onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
            onColumnResized={(e: ColumnResizedEvent) => {
              if (e.finished) saveState(e.api)
            }}
            onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
            rowHeight={40}
            headerHeight={40}
            suppressDragLeaveHidesColumns
            suppressCellFocus
          />
        </div>
      )}
    </div>
  )
}
